import React, { useEffect, useState } from "react";
import { Box, useTheme, Button, Switch } from "@mui/material";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import FlexBetween from "../../components/FlexBetween";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import RoleModal from "components/role/RoleModal";
import { Edit } from "@mui/icons-material";
import { deleteRole, getRole } from "service/permission/Role";
import ShowFullScreenBackdrop from "components/ShowFullScreenBackdrop";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Role = () => {
  const theme = useTheme();
  const [roleData, setRoleData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [roleEdit, setRoleEdit] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleRoleEdit = (roleData) => {
    setDialogOpen(true);
    setRoleEdit(roleData);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setRoleEdit({});
    }, 500);
  };

  const handleRoleIsActiveToggle = async (roleId, isActive) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to set this role as ${
          isActive === 1 ? "inactive" : "active"
        }?`
      );

      if (!confirmed) {
        return;
      }
      setOpen(true);

      await deleteRole(roleId, { isActive: isActive === 1 ? 0 : 1 });
      await fetchRoleList();

      toast.success(`Role is now ${isActive === 1 ? "inactive" : "active"}.`);
    } catch (error) {
      toast.error("Failed to update role status.");
    } finally {
      setOpen(false);
    }
  };

  const fetchRoleList = async () => {
    try {
      setIsLoading(true);
      const response = await getRole();
      setRoleData(response.data.data);
      // console.log(response.data.data);
    } catch (error) {
      console.error("There was a problem fetching the role list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleList();
  }, []);

  const column = [
    {
      field: "#",
      headerName: "S.NO",
      flex: 0.5,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "role",
      headerName: "ROLE",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "CREATED AT",
      flex: 1,
    },
    {
      field: "updatedAt",
      headerName: "UPDATED AT",
      flex: 1,
    },

    {
      field: "isActive",
      headerName: "STATUS",
      flex: 0.6,
      renderCell: (params) => {
        const isActive = params.value === 1;
        return (
          <Switch
            color="secondary"
            checked={isActive}
            onClick={() =>
              handleRoleIsActiveToggle(params.row._id, params.value)
            }
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      renderCell: (params) => {
        return (
          <>
            <Box sx={{ mr: 2, cursor: "pointer" }}>
              <Edit onClick={() => handleRoleEdit(params.row)} />
            </Box>
          </>
        );
      },
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <Header title="ROLES" subTitle="List of Roles" />
        <Box
          mt="40px"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <FlexBetween>
            <FlexBetween></FlexBetween>
            <Button
              sx={{
                mb: "0.5rem",
                width: "8rem",
              }}
              startIcon={<AddCircleIcon />}
              variant="contained"
              color="secondary"
              onClick={handleDialogOpen}
            >
              Add New
            </Button>
          </FlexBetween>
          <RoleModal
            open={dialogOpen}
            handleClose={handleDialogClose}
            handleData={roleEdit}
            fetchRoleList={fetchRoleList}
          />
          <DataGrid
            loading={isLoading || !roleData}
            getRowId={(row) => row._id}
            rows={roleData || []}
            columns={column}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>
      {open && <ShowFullScreenBackdrop open={open} />}
    </>
  );
};

export default Role;
